import { useState } from "react"
import { Alert, Box, Button, Card, CardContent, CircularProgress, Stack, TextField, Typography } from "@mui/material"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { Repeat } from "lib/@components/repeat"
import Iconify from "minimals-template/components/Iconify"
import { generateRuleFromText } from "routes/rule-sets/controller/generate-rule-from-text"
import { generate } from "library/guid/index.mjs"
import { AiGeneratedRulePreview } from "./ai-generated-rule-preview"

export function AiRuleHelper({ onApplyRules, conditionFields, skillMapId }) {
    const [text, setText] = useState("")
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [generatedRules, setGeneratedRules] = useState(null)
    const [expanded, setExpanded] = useState(false)

    const availableFields = (conditionFields || []).map(toFieldValue)

    return (
        <Card
            variant="outlined"
            sx={{ borderColor: "primary.light", borderStyle: "dashed", borderRadius: 2, borderWidth: 2 }}
        >
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <If value={expanded}>
                    <Stack spacing={2}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Iconify icon="mdi:auto-fix" sx={{ fontSize: 20, color: "primary.main" }} />
                            <Typography color="primary.main" sx={{ fontSize: 16, fontWeight: 700 }}>
                                AI Rule Builder
                            </Typography>
                        </Stack>
                        <TextField
                            value={text}
                            onChange={handleTextChange}
                            placeholder="Describe what you want to filter... e.g. 'Exclude all Red classification tasks from Electrical trade'"
                            multiline
                            minRows={2}
                            maxRows={4}
                            size="small"
                            fullWidth
                            disabled={loading}
                        />
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={
                                    <If value={loading}>
                                        <CircularProgress size={16} color="inherit" />
                                        <Else>
                                            <Iconify icon="mdi:auto-fix" />
                                        </Else>
                                    </If>
                                }
                                onClick={handleGenerate}
                                disabled={!text.trim() || loading}
                            >
                                Generate
                            </Button>
                            <Button variant="text" size="small" onClick={handleCancel} disabled={loading}>
                                Cancel
                            </Button>
                        </Stack>
                        <If value={error}>
                            <Alert severity="error" onClose={handleClearError}>
                                {error}
                            </Alert>
                        </If>
                        <If value={generatedRules?.length > 0}>
                            <Stack spacing={1}>
                                <Typography variant="caption" fontWeight={600} color="text.secondary">
                                    GENERATED RULES PREVIEW
                                </Typography>
                                <Repeat list={generatedRules} item={<AiGeneratedRulePreview />} />
                                <Stack direction="row" spacing={1}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<Iconify icon="mdi:check" />}
                                        onClick={handleApply}
                                    >
                                        Apply Rules
                                    </Button>
                                    <Button variant="outlined" size="small" onClick={handleDismissPreview}>
                                        Dismiss
                                    </Button>
                                </Stack>
                            </Stack>
                        </If>
                    </Stack>
                    <Else>
                        <Box sx={{ display: "flex", alignItems: "center", cursor: "pointer" }} onClick={handleExpand}>
                            <Iconify icon="mdi:auto-fix" sx={{ fontSize: 20, color: "primary.main", mr: 1 }} />
                            <Typography color="primary.main" sx={{ fontSize: 16, fontWeight: 500 }}>
                                Use AI to generate rules by typing a description
                            </Typography>
                        </Box>
                    </Else>
                </If>
            </CardContent>
        </Card>
    )

    function toFieldValue(f) {
        return f.value
    }

    function handleTextChange(e) {
        setText(e.target.value)
    }

    function handleExpand() {
        setExpanded(true)
    }

    function handleCancel() {
        setExpanded(false)
        setText("")
        setError(null)
        setGeneratedRules(null)
    }

    function handleClearError() {
        setError(null)
    }

    async function handleGenerate() {
        setLoading(true)
        setError(null)
        setGeneratedRules(null)
        try {
            const result = await generateRuleFromText({ text, availableFields, skillMapId })
            const rules = result?.rules || []
            if (rules.length === 0) {
                setError("AI could not generate rules from the given description. Try being more specific.")
                return
            }
            setGeneratedRules(rules)
        } catch (e) {
            setError(e.message || "Failed to generate rules")
        } finally {
            setLoading(false)
        }
    }

    function handleApply() {
        if (!generatedRules?.length) return
        const rulesWithIds = generatedRules.map(toEditableRule)
        onApplyRules(rulesWithIds)
        handleCancel()
    }

    function toEditableRule(rule) {
        return {
            id: generate(),
            name: rule.name || "",
            conditions: (rule.conditions || []).map(toEditableCondition),
            action: rule.action || { type: "include" },
        }
    }

    function toEditableCondition(condition) {
        return {
            _id: generate(),
            field: condition.field,
            operator: condition.operator,
            value: condition.value,
        }
    }

    function handleDismissPreview() {
        setGeneratedRules(null)
    }
}
